// Edición del maestro de ítems: un borrador POR ÍTEM y el cálculo de qué hay que
// mandar al servidor. Las pantallas llevan solo los ítems tocados; este módulo
// decide cuáles cambiaron de verdad.
//
// El uso de un ítem en las plantillas ("en cuáles está y con qué
// obligatoriedad") se edita junto con el ítem, pero el servidor solo quiere lo
// que CAMBIÓ: una plantilla que no se nombra no se toca, así que mandar el
// estado completo pisaría lo que otro Super Admin hizo entre medio.

import type { Obligatoriedad } from "./estructuraDePlantilla";
import { admiteRecurso, type ModoDeProduccion, type RecursoDelDocumento } from "./produccionDelItem";
import type {
  CambioDeUso,
  ItemDelMaestroEditable,
  MaestroItemConUso,
  TipoDeContenido,
} from "./plantillas";

/** "" es "no incluido en esa plantilla". */
export type UsoEditable = Obligatoriedad | "";

export interface EdicionDeItemDelMaestro {
  /** El id como texto, o `nuevo:…` para uno que todavía no existe. */
  clave: string;
  grupo: string;
  /** "" es "sin subgrupo". */
  subgrupo: string;
  nombre: string;
  descripcion: string;
  tipo: TipoDeContenido;
  fuente: string;
  condicionSugerida: string;
  /** El modo CONFIRMADO; `null` es «nadie lo confirmó» (la pantalla sugiere uno). */
  modo: ModoDeProduccion | null;
  /** Pestaña de origen de un «texto ya validado»; "" es «sin elegir». */
  seccionOrigen: string;
  seRedactaAlFinal: boolean;
  /** Redacción estándar de un «Texto fijo»; "" es «sin texto». */
  textoFijo: string;
  /** Qué pieza del expediente muestra (sistema, tabla, cálculo, imagen); "" es «sin elegir». */
  recurso: RecursoDelDocumento | "";
  /** Ids de los ítems de los que depende (solo si lo redacta la IA). */
  dependencias: number[];
  /** id de plantilla → obligatoriedad, con una entrada por cada plantilla. */
  uso: Record<string, UsoEditable>;
}

/** Clic en una píldora de uso: "" → O → C → P → "". */
export function siguienteUso(actual: UsoEditable): UsoEditable {
  if (actual === "") return "obligatorio";
  if (actual === "obligatorio") return "condicional";
  if (actual === "condicional") return "opcional";
  return "";
}

function usoVacio(plantillas: number[]): Record<string, UsoEditable> {
  return Object.fromEntries(plantillas.map((id) => [String(id), "" as UsoEditable]));
}

export function edicionDesdeItem(
  item: MaestroItemConUso,
  plantillas: number[],
): EdicionDeItemDelMaestro {
  return {
    clave: String(item.id),
    grupo: item.grupo,
    subgrupo: item.subgrupo ?? "",
    nombre: item.nombre,
    descripcion: item.descripcion ?? "",
    tipo: item.tipo_contenido,
    fuente: item.fuente ?? "",
    condicionSugerida: item.condicion_sugerida ?? "",
    modo: item.modo_produccion,
    seccionOrigen: item.seccion_origen ?? "",
    seRedactaAlFinal: item.se_redacta_al_final,
    textoFijo: item.texto_fijo ?? "",
    recurso: item.recurso ?? "",
    dependencias: [...item.dependencias],
    uso: { ...usoVacio(plantillas), ...item.uso },
  };
}

export function edicionNueva(
  clave: string,
  grupo: string,
  subgrupo: string,
  plantillas: number[],
): EdicionDeItemDelMaestro {
  return {
    clave,
    grupo,
    subgrupo,
    nombre: "Nuevo ítem",
    descripcion: "",
    tipo: "texto",
    fuente: "Tasador",
    condicionSugerida: "",
    modo: null,
    seccionOrigen: "",
    seRedactaAlFinal: false,
    textoFijo: "",
    recurso: "",
    dependencias: [],
    uso: usoVacio(plantillas),
  };
}

/** Grupos del maestro en su orden, sin repetir. */
export function gruposDelMaestro(items: { grupo: string }[]): string[] {
  return [...new Set(items.map((i) => i.grupo))];
}

/** Subgrupos con nombre de un grupo, en su orden. */
export function subgruposDeUnGrupo(
  items: { grupo: string; subgrupo: string | null }[],
  grupo: string,
): string[] {
  return [
    ...new Set(
      items.filter((i) => i.grupo === grupo && i.subgrupo).map((i) => i.subgrupo as string),
    ),
  ];
}

export type CambioDelMaestro =
  | { tipo: "actualizar"; id: number; datos: ItemDelMaestroEditable & { uso?: CambioDeUso } }
  | {
      tipo: "crear";
      clave: string;
      datos: ItemDelMaestroEditable;
      /** Plantillas donde entra: se aplican con un PUT después del alta. */
      uso: CambioDeUso;
    };

function datosDe(e: EdicionDeItemDelMaestro): ItemDelMaestroEditable {
  // Lo que no corresponde al modo viaja vacío: el servidor lo descartaría igual,
  // pero así lo que se manda dice lo mismo que lo que la pantalla muestra.
  const esIa = e.modo === "redactado_ia";

  return {
    grupo: e.grupo,
    subgrupo: e.subgrupo.trim() === "" ? null : e.subgrupo,
    nombre: e.nombre,
    descripcion: e.descripcion,
    tipo_contenido: e.tipo,
    fuente: e.fuente,
    condicion_sugerida: e.condicionSugerida === "" ? null : e.condicionSugerida,
    modo_produccion: e.modo,
    seccion_origen: e.modo === "texto_validado" && e.seccionOrigen ? e.seccionOrigen : null,
    se_redacta_al_final: esIa && e.seRedactaAlFinal,
    dependencias: esIa ? e.dependencias : [],
    texto_fijo: e.modo === "texto_fijo" && e.textoFijo.trim() !== "" ? e.textoFijo : null,
    recurso: e.modo !== null && admiteRecurso(e.modo) && e.recurso !== "" ? e.recurso : null,
  };
}

/** Las dependencias son un conjunto: el orden en que se eligieron no es un cambio. */
function mismasDependencias(a: number[], b: number[]): boolean {
  return a.length === b.length && [...a].sort().join() === [...b].sort().join();
}

function camposIguales(a: EdicionDeItemDelMaestro, b: EdicionDeItemDelMaestro): boolean {
  return (
    a.grupo === b.grupo &&
    a.subgrupo === b.subgrupo &&
    a.nombre === b.nombre &&
    a.descripcion === b.descripcion &&
    a.tipo === b.tipo &&
    a.fuente === b.fuente &&
    a.condicionSugerida === b.condicionSugerida &&
    a.modo === b.modo &&
    a.seccionOrigen === b.seccionOrigen &&
    a.seRedactaAlFinal === b.seRedactaAlFinal &&
    a.textoFijo === b.textoFijo &&
    a.recurso === b.recurso &&
    mismasDependencias(a.dependencias, b.dependencias)
  );
}

/**
 * Qué mandar al servidor: lo que de verdad cambió respecto de los ítems
 * originales, en el orden en que se tocaron. Las ediciones idénticas a su
 * original no cuentan, así que abrir un ítem y cerrarlo no deja "cambios".
 */
export function cambiosPendientesDelMaestro(
  originales: MaestroItemConUso[],
  borradores: Record<string, EdicionDeItemDelMaestro>,
  plantillas: number[],
): CambioDelMaestro[] {
  const cambios: CambioDelMaestro[] = [];

  for (const [clave, edicion] of Object.entries(borradores)) {
    const original = originales.find((o) => String(o.id) === clave);

    if (!original) {
      const uso: CambioDeUso = {};
      for (const [plantilla, valor] of Object.entries(edicion.uso)) {
        if (valor !== "") uso[plantilla] = valor;
      }
      cambios.push({ tipo: "crear", clave, datos: datosDe(edicion), uso });
      continue;
    }

    const base = edicionDesdeItem(original, plantillas);
    const uso: CambioDeUso = {};
    for (const [plantilla, valor] of Object.entries(edicion.uso)) {
      if (valor === (base.uso[plantilla] ?? "")) continue;
      uso[plantilla] = valor === "" ? null : valor;
    }

    const cambiaUso = Object.keys(uso).length > 0;
    if (camposIguales(base, edicion) && !cambiaUso) continue;

    cambios.push({
      tipo: "actualizar",
      id: original.id,
      datos: { ...datosDe(edicion), ...(cambiaUso ? { uso } : {}) },
    });
  }

  return cambios;
}
